/**
 * Web Push subscription helper.
 * - Fetches VAPID public key from backend
 * - Prompts the browser for notification permission
 * - Subscribes via the SW's PushManager and sends the subscription to backend
 */
import api from "@/lib/api";

import { devError } from "@/lib/devLog";
function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function getPushStatus() {
  if (!(await isPushSupported())) return "unsupported";
  const perm = Notification.permission;
  if (perm === "denied") return "denied";
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return "no-sw";
    const sub = await reg.pushManager.getSubscription();
    if (sub && perm === "granted") return "subscribed";
    return perm === "granted" ? "granted" : "prompt";
  } catch { return "prompt"; }
}

/**
 * Compare an existing subscription's applicationServerKey with a fresh key.
 * Chrome returns "Registration failed - push server error" when we call
 * pushManager.subscribe() while an existing subscription for this origin
 * uses a DIFFERENT VAPID key. We must unsubscribe first in that case.
 */
function keysDiffer(existingSub, freshKeyBytes) {
  const opts = existingSub?.options;
  if (!opts || !opts.applicationServerKey) return true;
  const existingBytes = new Uint8Array(opts.applicationServerKey);
  if (existingBytes.length !== freshKeyBytes.length) return true;
  for (let i = 0; i < existingBytes.length; i += 1) {
    if (existingBytes[i] !== freshKeyBytes[i]) return true;
  }
  return false;
}

export async function subscribePush() {
  if (!(await isPushSupported())) throw new Error("Push notifications not supported in this browser");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied");
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  const key = urlB64ToUint8Array(data.public_key);

  // Reuse existing subscription IFF its applicationServerKey matches the
  // current backend VAPID key. Otherwise force a clean re-subscribe.
  let sub = await reg.pushManager.getSubscription();
  if (sub && keysDiffer(sub, key)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
    } catch (err) {
      // Surface the underlying browser error name so we can distinguish
      // permission blocks from FCM/GCM push-service failures.
      const name = err?.name || "PushError";
      const msg = err?.message || String(err);
      devError("[push] subscribe failed", name, msg);
      throw new Error(`${name}: ${msg}`);
    }
  }
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    keys: sub.toJSON().keys,
  });
  return sub;
}

export async function unsubscribePush() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe();
}
